import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { startOfDay, endOfDay, startOfYear, subDays } from "date-fns";
import Button from "@components/Form/Button";
import Calendar from "@components/Form/Calendar";
import { Calendar as CalendarIcon } from "@assets/icons";
import { useCalendarDropdown } from "../CalendarDropdown/useCalendarDropdown";
import styles from "./DateRangeFilter.module.scss";

export const dateRangePresets = [
  {
    name: "principal.statistic.today",
    dtStart: startOfDay(new Date()).getTime(),
    dtEnd: endOfDay(new Date()).getTime(),
  },
  {
    name: "principal.statistic.last7Days",
    dtStart: startOfDay(subDays(new Date(), 6)).getTime(),
    dtEnd: endOfDay(new Date()).getTime(),
  },
  {
    name: "principal.statistic.last30Days",
    dtStart: startOfDay(subDays(new Date(), 29)).getTime(),
    dtEnd: endOfDay(new Date()).getTime(),
  },
  {
    name: "principal.statistic.last90Days",
    dtStart: startOfDay(subDays(new Date(), 89)).getTime(),
    dtEnd: endOfDay(new Date()).getTime(),
  },
  {
    name: "principal.statistic.thisYear",
    dtStart: startOfYear(new Date()).getTime(),
    dtEnd: endOfDay(new Date()).getTime(),
  },
  {
    name: "principal.statistic.allTime",
    dtStart: null,
    dtEnd: null,
    isAllTime: true,
  },
];

const DateRangeFilter = ({
  presets,
  selectedPreset,
  onPresetSelect,
  calendarValue,
  onCustomRangeSelect,
  triggerLabel,
  buttonClassName,
  sidebarClassName,
  iconWidth,
  iconHeight,
}) => {
  const { t } = useTranslation();
  const { isOpen, position, wrapperRef, triggerRef, calendarRef, toggle } =
    useCalendarDropdown({ width: 640 });

  return (
    <div className="relative" ref={wrapperRef}>
      <div ref={triggerRef}>
        <Button
          variant="custom"
          onClick={toggle}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          className={clsx(
            "rounded-lg bg-cultured px-4 py-3 whitespace-nowrap",
            buttonClassName,
          )}
          contentClassName="gap-2"
        >
          <CalendarIcon width={iconWidth} height={iconHeight} />
          <span className="font-medium-poppins text-dawn-gray">
            {triggerLabel}
          </span>
        </Button>
      </div>
      {createPortal(
        <AnimatePresence>
          {isOpen && (
            <motion.div
              ref={calendarRef}
              role="dialog"
              aria-label={triggerLabel}
              style={{
                position: "fixed",
                top: position.top,
                left: position.left,
                zIndex: 9999,
              }}
              className={clsx(
                "rounded-md bg-milk",
                styles.calendarShadow,
                styles.calendarPanel,
              )}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className={clsx(styles.calendarRightBar, sidebarClassName)}>
                {presets.map((item, index) => (
                  <button
                    type="button"
                    onClick={() => onPresetSelect(item)}
                    key={`${item.name}-${index}`}
                    className={clsx(
                      styles.calendarRightBarItem,
                      "whitespace-nowrap py-3 cursor-pointer text-dawn-gray",
                      selectedPreset?.name === item.name &&
                        "bg-pampas font-medium-poppins",
                    )}
                  >
                    {t(item.name)}
                  </button>
                ))}
              </div>
              <Calendar
                onChange={onCustomRangeSelect}
                withoutShadow
                selectRange
                value={calendarValue}
                defaultActiveStartDate={
                  calendarValue?.[1] ? new Date(calendarValue[1]) : new Date()
                }
                className={styles.calendar}
              />
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </div>
  );
};

export default DateRangeFilter;
